import { useState, useEffect } from "react";
import axios from "axios";
import { Pulse, Users, ChartLineUp, Rocket, Scales } from "@phosphor-icons/react";
import { Tip } from "./commerceUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (v) => (v == null ? "—" : `${v}%`);
const num = (v) => (v == null ? "—" : v);

const tone = (v, good = 70, ok = 40) =>
  v == null ? "text-zinc-300" : v >= good ? "text-emerald-600" : v >= ok ? "text-amber-600" : "text-rose-500";

const Tile = ({ label, value, sub, valueClass, tip, testid }) => (
  <div className="rounded-2xl bg-white/85 backdrop-blur border border-violet-100 p-5 shadow-sm" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 flex items-center">
      {label}{tip && <Tip text={tip} />}
    </div>
    <div className={`mt-2 text-3xl font-black leading-none ${valueClass || "text-zinc-900"}`}>{value}</div>
    {sub && <div className="mt-1 text-[11px] text-zinc-400">{sub}</div>}
  </div>
);

const PipelineHealth = () => {
  const [h, setH] = useState(null);

  useEffect(() => {
    axios.get(`${API}/commerce/pipeline/health`).then(({ data }) => setH(data)).catch(() => setH(false));
  }, []);

  if (h === null) return <div className="text-center py-16 text-zinc-400" data-testid="health-loading">Loading pipeline health…</div>;
  if (h === false) return <div className="text-center py-16 text-rose-400" data-testid="health-error">Couldn't load pipeline health.</div>;

  const m = h.metrics;
  const esp = h.evidence_strength_predictiveness;
  const rp = h.review_progress;

  return (
    <div data-testid="commerce-pipeline-health" className="space-y-6">
      {/* Review progress */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 p-4" data-testid="health-progress">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-bold text-violet-700 uppercase tracking-wide flex items-center gap-1.5"><Pulse size={14} weight="fill" /> Review Progress</span>
          <span className="text-zinc-500">{rp.reviewed} / {rp.total} reviewed ({rp.pct}%)</span>
        </div>
        <div className="h-2 rounded-full bg-white overflow-hidden border border-violet-100">
          <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${rp.pct}%` }} />
        </div>
      </div>

      {/* Foundation quality */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3 flex items-center gap-1.5"><Users size={13} weight="fill" /> Foundation Quality</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile testid="health-cv-coverage" label="Customer Voice Coverage" value={pct(m.customer_voice_coverage_pct)} valueClass={tone(m.customer_voice_coverage_pct, 60, 40)} sub={`${m.voice_backed_opportunities} voice-backed`} tip="Share of opportunities backed by real customer complaints (not just search intent)." />
          <Tile testid="health-avg-es" label="Avg Evidence Strength" value={num(m.average_evidence_strength)} valueClass={tone(m.average_evidence_strength)} tip="Average multi-source corroboration strength across all opportunities." />
          <Tile testid="health-strong-es" label="Strong-Evidence Opportunities" value={num(m.strong_evidence_opportunities)} sub="Evidence Strength ≥ 70" />
          <Tile testid="health-search-only" label="Search-only Opportunities" value={num(m.search_only_opportunities)} valueClass="text-amber-600" sub="No customer voice yet" tip="Opportunities driven by search intent alone — weaker foundation." />
        </div>
      </div>

      {/* Decision quality */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3 flex items-center gap-1.5"><Scales size={13} weight="fill" /> Decision Quality</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile testid="health-avg-dc" label="Avg Decision Confidence" value={num(m.average_decision_confidence)} valueClass={tone(m.average_decision_confidence)} tip="How confident PrimePillar is (on average) that opportunities should become businesses." />
          <Tile testid="health-build-rate" label="Human Build Rate" value={pct(m.human_build_rate_pct)} valueClass="text-emerald-600" sub={`${rp.reviewed} reviewed`} />
          <Tile testid="health-reject-rate" label="Human Reject Rate" value={pct(m.human_reject_rate_pct)} valueClass="text-rose-500" />
          <Tile testid="health-ai-agreement" label="AI vs Human Agreement" value={pct(m.ai_vs_human_agreement_pct)} valueClass={tone(m.ai_vs_human_agreement_pct)} tip="How often the AI Business Assessment matched your Build/Watch/Reject decisions." />
        </div>
      </div>

      {/* Does the engine's signal predict your decisions? */}
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3 flex items-center gap-1.5"><ChartLineUp size={13} weight="fill" /> Does Evidence Predict Build?</div>
        <div className="grid grid-cols-3 gap-3" data-testid="health-predictiveness">
          <Tile label="Avg ES · Build" value={num(esp.avg_es_of_builds)} valueClass="text-emerald-600" />
          <Tile label="Avg ES · Watch" value={num(esp.avg_es_of_watch)} valueClass="text-amber-600" />
          <Tile label="Avg ES · Reject" value={num(esp.avg_es_of_rejects)} valueClass="text-rose-500" />
        </div>
        <p className="mt-2 text-[11px] text-zinc-400">If Build opportunities have clearly higher Evidence Strength than Rejects, the metric is predictive. Populates as you review.</p>
      </div>

      {/* Phase 5 readiness + best markets */}
      <div className="grid lg:grid-cols-2 gap-3">
        <Tile testid="health-phase5-ready" label="Opportunities Ready for Phase 5" value={num(m.opportunities_ready_for_phase5)} valueClass="text-violet-600"
          sub="Marked Build — Phase 5 runs only on these" />
        <div className="rounded-2xl bg-white/85 backdrop-blur border border-violet-100 p-5 shadow-sm" data-testid="health-best-markets">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 flex items-center gap-1.5"><Rocket size={12} weight="fill" /> Best Markets by Build Quality</div>
          {h.best_markets_by_build_quality.length === 0
            ? <div className="mt-2 text-[11px] text-zinc-400">No Build decisions yet — mark opportunities in the Review Workspace.</div>
            : <div className="mt-2 flex flex-wrap gap-2">
                {h.best_markets_by_build_quality.map((mk) => (
                  <span key={mk.market} className="text-[11px] px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold">
                    {mk.market} · {mk.avg_decision_confidence} ({mk.builds})
                  </span>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
};

export default PipelineHealth;
